import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Link, Typography, Divider, Grid } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { useReactToPrint } from 'react-to-print';
import LoadingScreen from '@lwtears/lwt-common-frontend/lib/LoadingScreen';
import { parseQueryParams } from '@lwtears/lwt-common-frontend/lib/@common/util/query-params-util';
import { translate } from '@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import {
  AssessmentType,
  BenchmarkAssessment,
  Checklist,
  Domain,
  StudentChecklistOutcome,
  UnitAssessment,
  UnitChecklist,
  WeeklyChecklist,
} from 'types';
import GET_STUDENT_CHECKLIST_OUTCOMES from 'graph/queries/checklists/get-student-checklist-outcomes';
import useChecklistsStore from 'graph/state/query-hooks/use-checklists';
import getClassroomStore from 'graph/state/query-hooks/use-get-classroom';
import useStudentStore from 'graph/state/query-hooks/use-student';
import useAssessmentStore from 'graph/state/query-hooks/use-assessment';
import useAssessmentReportStore from 'graph/state/query-hooks/use-assessment-report';
import AssessmentScore from 'components/common/AssessmentScore';
import PrintAssessmentDetails from '../PrintAssessmentDetails';
import OutlineButton from 'components/common/Buttons/OutlineButton';
import DomainSelectionList from '../Assessment/components/DomainSelectionList';
import { NotepadSrc } from 'components/common/NotepadSrc';
import { getCurriculumBenchmarkAssessment } from 'util/assessments-util';
import { getCurriculumBenchmarkChecklists } from 'util/checklists-util';

const t = (key) => translate('ui', key);

const useStyles = makeStyles((theme: Theme) => {
  return {
    dFlex: {
      display: 'flex',
      alignItems: 'flex-start',
    },
    pt4: {
      paddingTop: theme.spacing(4),
    },
    mainContainer: {
      paddingLeft: theme.spacing(30),
      paddingRight: theme.spacing(30),
      paddingBottom: theme.spacing(10),
    },
    pageHeader: {
      display: 'flex',
      paddingTop: theme.spacing(4),
      paddingBottom: theme.spacing(2),
      alignItems: 'flex-end',
      justifyContent: 'space-between',
    },
    secondaryLink: {
      textDecoration: 'none !important',
      color: theme.palette.black.main + ' !important',
      marginLeft: theme.spacing(4),
      fontFamily: 'HWT Futura',
      '&:hover': {
        textDecoration: 'underline !important',
      },
    },
    section: {
      backgroundColor: 'white',
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(8),
      padding: theme.spacing(4),
      border: `1px solid ${(theme.palette as any).grey[600]}`,
    },
    sectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',

      '&>h2': {
        color: theme.palette.primary.main,
      },
    },
    ul: {
      listStyle: 'none',
      padding: 0,
    },
    twoColumnGrid: {
      display: 'grid',
      gridTemplateColumns: '50% 50%',
      marginTop: theme.spacing(4),

      '&>li': {
        display: 'grid',
        gridTemplateColumns: '1fr .25fr',
        gridColumnGap: theme.spacing(4),
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        fontFamily: 'HWT Futura',

        '&:not(:nth-last-child(-n+2)),&:nth-last-child(2):nth-child(even)': {
          borderBottom: `1px solid ${(theme.palette as any).grey[600]}`,
        },
      },
    },
    vertTab: {
      height: '100%',
      margin: '0',
      borderLeft: `3px solid ${(theme.palette as any).grey[400]}`,

      '&>li': {
        paddingLeft: theme.spacing(4),
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        marginLeft: '-3px',
        borderLeft: '3px solid transparent',

        '&>a': {
          color: theme.palette.black.main,
        },
      },
    },
  };
});

const ViewUnitAssessments: React.FC = () => {
  const { assessmentId, studentId, classroomId, curriculumId } = parseQueryParams(
    window.location.search
  );

  const classes = useStyles();
  const navigate = useNavigate();

  if (
    !assessmentId ||
    !Number(assessmentId) ||
    !studentId ||
    !Number(studentId) ||
    !classroomId ||
    !Number(classroomId) ||
    !curriculumId ||
    !Number(curriculumId)
  ) {
    navigate('/404');
  }

  const [classroom] = getClassroomStore(Number(classroomId));
  const [student] = useStudentStore(Number(studentId));
  const [assessment] = useAssessmentStore({ where: { id: Number(assessmentId) } });
  const [assessmentReport] = useAssessmentReportStore({
    where: { assessmentId: Number(assessmentId), studentId: Number(studentId) },
  });
  const [{ checklists, isLoading: isChecklistsLoading }] = useChecklistsStore();
  const [checklist, setChecklist] = useState<Partial<Checklist>>();
  const [domainId, setDomainId] = useState<number>();
  const [uniqueDomains, setUniqueDomains] = useState<Partial<Domain>[]>();
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const productCurriculumId = Number(curriculumId);

  const reference = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    content: () => reference.current,
  });

  const benchmarkChecklists = useMemo(
    () => getCurriculumBenchmarkChecklists(checklists, productCurriculumId),
    [checklists, productCurriculumId]
  );

  // set active checklist whenever the list of checklists or the assessment changes
  useEffect(() => {
    // match a checklist to the assessment
    // if the assessment is a UnitAssessment, match on unitId
    if (assessment.assessment?.type === AssessmentType.Unit) {
      setChecklist(
        (checklists as UnitChecklist[])?.find(
          (checklist) =>
            checklist.type === AssessmentType.Unit &&
            checklist.unitId === (assessment.assessment as UnitAssessment).unitId
        )
      );
    } else if (assessment.assessment?.type === AssessmentType.Benchmark) {
      // if the assessment is a benchmark, match on productCurriculumId + period
      const benchmarkChecklist = getCurriculumBenchmarkAssessment(
        assessment.assessment as BenchmarkAssessment,
        productCurriculumId
      );

      setChecklist(
        benchmarkChecklists?.find(
          (checklist) =>
            checklist.type === AssessmentType.Benchmark &&
            checklist.productCurriculumId === benchmarkChecklist.productCurriculumId &&
            checklist.period === benchmarkChecklist.period
        )
      );
    }
  }, [checklists, assessment.assessment, productCurriculumId, benchmarkChecklists]);

  // change the domain whenever the active assessment changes
  useEffect(() => {
    // set the domain to the first one found in the assessment
    setDomainId(assessment.assessment?.assessmentDomains?.at(0)?.domain.id);
  }, [assessment.assessment]);

  // change the list of unique domains whenever the active checklist or assessment changes
  useEffect(() => {
    const allDomains = [
      ...(checklist?.checklistObservations?.map((obs) => obs?.observation?.domain) || []),
      ...(assessment.assessment?.assessmentDomains?.map((dom) => dom?.domain) || []),
    ].filter((dom) => dom !== undefined);
    setUniqueDomains(
      allDomains.reduce((acc, curr) => {
        if (!acc.some((dom) => curr.id === dom.id)) {
          acc.push(curr);
        }
        return acc;
      }, new Array<Domain>())
    );
  }, [assessment.assessment, checklist]);

  const domainsForList: { title: string; id: number }[] =
    uniqueDomains?.map((domain) => ({
      title: domain.title!,
      id: domain.id!,
    })) || [];

  // call useQuery directly to simplify files/calls, since we are providing everything in args (and thus, not really re-usable)
  const checklistOutcomes: Partial<StudentChecklistOutcome>[] | undefined = useQuery(
    GET_STUDENT_CHECKLIST_OUTCOMES,
    {
      variables: {
        where: {
          checklistId: checklist?.id,
          studentId: Number(studentId),
        },
      },
      skip: checklist?.id === undefined,
    }
  ).data?.studentChecklistOutcomes;

  const assessmentSections = assessment.assessment?.assessmentDomains
    ?.filter((dom) => domainId && dom.domain.id === domainId)
    .at(0)?.assessmentSections;

  if (
    classroom.isLoading ||
    student.isLoading ||
    isChecklistsLoading ||
    assessment.isLoading ||
    assessmentReport.isLoading
  ) {
    return <LoadingScreen backgroundImage={NotepadSrc} />;
  } else if (!assessment.assessment || !classroom.data || !student.student) {
    navigate('/404');
  }

  const handleDomainSelection = (index: number) => {
    setSelectedIndex(index);
    setDomainId(domainsForList.at(index)?.id);
  };

  const checklistForDomain = checklistOutcomes?.filter(
    (outcome) => outcome?.observation?.domainId === domainId
  );

  return (
    <div className={classes.mainContainer} style={{ flexGrow: 1 }}>
      <div style={{ display: 'none' }}>
        <PrintAssessmentDetails
          assessment={assessment.assessment!}
          curriculumId={productCurriculumId}
          student={student.student!}
          domains={uniqueDomains}
          assessmentReport={assessmentReport.assessmentReport}
          weeklyChecklists={
            assessment.assessment!.type === AssessmentType.Unit
              ? checklists?.filter(
                  (v) =>
                    v.type === AssessmentType.Week &&
                    (v as WeeklyChecklist).unitId ===
                      (assessment.assessment as UnitAssessment).unitId
                )
              : undefined
          }
          checklistOutcomes={checklistOutcomes}
          ref={reference}
        />
      </div>
      <div className={classes.pageHeader}>
        <Typography variant="h1" component="h1">
          {classroom.data?.name}
        </Typography>
        <Link
          component={RouterLink}
          className={classes.secondaryLink}
          to={`/class/${classroomId}?tabSelection=${
            assessment.assessment?.type === AssessmentType.Benchmark ? 'Benchmark' : 'Unit'
          }`}
        >
          {t('assessments.scores')}
        </Link>
      </div>
      <Divider />
      <section className={classes.section}>
        <Grid container item xs={12} spacing={4}>
          <Grid item xs={8}>
            <div className={classes.sectionHeader}>
              <Typography variant="h2" component="h2">
                {`${t('assessments.student')} ${student?.student?.firstName} ${
                  student?.student?.lastName
                }: ${
                  assessment.assessment?.type === AssessmentType.Unit
                    ? (assessment.assessment as UnitAssessment)?.unit?.title
                    : assessment.assessment?.type === AssessmentType.Benchmark &&
                      `${t(
                        'assessments.benchmarkPeriod-' +
                          getCurriculumBenchmarkAssessment(
                            assessment.assessment as BenchmarkAssessment,
                            productCurriculumId
                          ).period
                      )}`
                } ${t('assessments.assessment')}`}
              </Typography>
              <div className={classes.dFlex}>
                <Link
                  underline="none"
                  component={RouterLink}
                  to={`/assessment/${assessmentId}?studentId=${studentId}&classroomId=${classroomId}`}
                >
                  <OutlineButton>{t('assessments.edit')}</OutlineButton>
                </Link>
                <OutlineButton onClick={handlePrint}>{t('assessments.print')}</OutlineButton>
              </div>
            </div>
            {assessmentSections?.length && (
              <div>
                <Typography variant="h3" className={classes.pt4}>
                  {t('assessments.demonstrations')}
                </Typography>
                <ul className={clsx(classes.ul, classes.twoColumnGrid)}>
                  {assessmentSections?.map((section) => (
                    <li key={section.id}>
                      <span>{section.title}</span>
                      {
                        <AssessmentScore
                          score={
                            assessmentReport.assessmentReport?.reportSections
                              ?.find((stat) => stat.assessmentSectionId === section.id)
                              ?.stats?.at(0)?.score
                          }
                        />
                      }
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {checklistForDomain && checklistForDomain.length > 0 && (
              <div>
                <Typography variant="h3" className={classes.pt4}>
                  {t('assessments.observations')}
                </Typography>
                <ul className={clsx(classes.ul, classes.twoColumnGrid)}>
                  {checklistForDomain.map((outcome) => (
                    <li key={outcome.observation?.id}>
                      <span>{outcome.observation?.title}</span>
                      {<AssessmentScore score={outcome.score} />}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Grid>
          <Grid item xs={4}>
            {assessmentReport.assessmentReport && (
              <DomainSelectionList
                domains={domainsForList}
                selectedIndex={selectedIndex}
                assessmentReport={assessmentReport.assessmentReport}
                callbackFn={handleDomainSelection}
              />
            )}
          </Grid>
        </Grid>
      </section>
    </div>
  );
};

export default ViewUnitAssessments;
